import { USER_INFO_KEY } from './constants.js';
import { picoApiManager } from './network/picoApiManager.js';
import {} from './pico.js';
import Mutex from './utils/mutex.js';
import { timeoutScheduler } from './utils/scheduler.js';
import { timestampGenerator } from './utils/timestamps.js';
const overrideDefaultConfig = (config) => {
    const defaultLogger = {
        trace: () => { },
        info: () => { },
        debug: () => { },
        warn: () => { },
        error: () => { },
        fatal: () => { },
    };
    const noOpSessionManager = {
        isEnabled: () => false,
        isNewSession: () => false,
        getSessionStartId: () => '',
        getSessionStart: () => 0,
        getSessionId: () => '123',
    };
    const defaults = {
        apiManager: picoApiManager({
            bspId: config.bspId,
            environment: config.environment ?? { type: 'production' },
            logger: config.logger ?? defaultLogger,
            timestampGenerator: config.timestampGenerator ?? timestampGenerator,
        }),
        batchDeleteSizeWhenFull: 200,
        environment: { type: 'production' },
        eventProcessors: [],
        eventsBatchSize: 10,
        logger: defaultLogger,
        maxEventsStored: 1000,
        maxExponentialBackoffInterval: 5 * 60 * 1000,
        scheduler: timeoutScheduler(),
        sessionManager: noOpSessionManager,
        sendNextBatchInterval: 2 * 1000,
        timestampGenerator: timestampGenerator,
        userInfo: {},
    };
    return { ...defaults, ...config };
};
/**
 * Create a pico instance that can be used to track user actions.
 *
 * @param clientConfig configuration for the pico client.
 * @returns a promise that resolves to a pico client instance.
 */
export const createPico = async (clientConfig) => {
    try {
        const config = overrideDefaultConfig(clientConfig);
        // The next batch interval is used to schedule the next sending cycle.
        let nextBatchInterval = config.sendNextBatchInterval;
        const sendNextBatchMutex = new Mutex();
        const sendNextBatch = async () => {
            // This mutex is needed to sync calls to `sendNextBatch` within the same tab.
            // Normally only one request at a time should be either scheduled or running, this mutex makes sure to
            // queue requests in case they are concurrent avoiding sending the same events multiple times.
            return sendNextBatchMutex.run(async () => {
                config.logger.debug('Sending events batch.');
                try {
                    const eventCount = await config.persistenceManager.countEvents();
                    if (eventCount > 0) {
                        const eventsToSend = await config.persistenceManager.getEvents(config.eventsBatchSize);
                        await config.apiManager.sendEvents(eventsToSend);
                        await config.persistenceManager.deleteEvents(eventsToSend);
                    }
                    else {
                        config.logger.debug('No events found.');
                    }
                    if (config.sendNextBatchInterval === 0 && eventCount > config.eventsBatchSize) {
                        // Configured in no-batch mode but there are remaining pending events, schedule another send immediately.
                        // This can happen after coming online if many events have been added in the meantime.
                        nextBatchInterval = 1;
                    }
                    else {
                        nextBatchInterval = config.sendNextBatchInterval;
                    }
                }
                catch (e) {
                    config.logger.error('An error occurred while sending events.', e);
                    nextBatchInterval = 2 * (nextBatchInterval !== 0 ? nextBatchInterval : 10 * 1000);
                    nextBatchInterval = Math.min(nextBatchInterval, config.maxExponentialBackoffInterval);
                }
                finally {
                    if (nextBatchInterval > 0) {
                        config.scheduler.scheduleAction(sendNextBatch, nextBatchInterval, 'sendEvents');
                    }
                }
            });
        };
        const extractUserIds = async (userIdGenerators) => {
            const userIds = {};
            for (const key in userIdGenerators) {
                const generator = userIdGenerators[key];
                if (generator) {
                    userIds[key] = await generator();
                }
            }
            return userIds;
        };
        const processAndPersistEvent = async (type, event) => {
            const userIds = await extractUserIds(config.userIdGenerators);
            const userInfo = (await config.persistenceManager.getValue(USER_INFO_KEY)) ?? {};
            let sessionData = {};
            if (config.sessionManager.isEnabled()) {
                sessionData = {
                    sessionId: config.sessionManager.getSessionId(),
                    sessionStartId: config.sessionManager.getSessionStartId(),
                    secondsFromSessionStart: config.timestampGenerator() - config.sessionManager.getSessionStart(),
                };
            }
            let eventWithMetadata = {
                ...event,
                ...sessionData,
                id: config.uuidFactory(),
                timestamp: config.timestampGenerator(),
                type,
                userIds: userIds,
                userInfo: userInfo,
            };
            for (const processor of config.eventProcessors) {
                const processedEvent = processor.processEvent(eventWithMetadata);
                if (processedEvent) {
                    eventWithMetadata = processedEvent;
                }
                else {
                    return;
                }
            }
            await config.persistenceManager.saveEvent(eventWithMetadata);
        };
        const shouldSendExperimentSegmentReceivedEvent = (oldSegments, newSegments) => {
            const newSegmentsKeys = Object.keys(newSegments);
            const oldSegmentsKeys = Object.keys(oldSegments);
            if (newSegmentsKeys.length !== oldSegmentsKeys.length) {
                return true;
            }
            for (const key of newSegmentsKeys) {
                if (newSegments[key] !== oldSegments[key]) {
                    return true;
                }
            }
            return false;
        };
        const persistantInfoUpdateMutex = new Mutex();
        const getUserInfo = async () => (await config.persistenceManager.getValue(USER_INFO_KEY)) ?? {};
        const mergeAndPersistUserInfo = async (newMetadata) => {
            // Since context switches may happen between getValue (called in getUserInfo) and saveValue, we need to ensure
            // that the operation is atomic, otherwise we may lose some updates.
            await persistantInfoUpdateMutex.run(async () => {
                try {
                    const userInfo = await getUserInfo();
                    const mergedUserInfo = { ...userInfo, ...newMetadata };
                    await config.persistenceManager.saveValue(USER_INFO_KEY, mergedUserInfo);
                }
                catch (e) {
                    config.logger.error('An error occurred while merging and persisting user info.', e);
                }
            });
        };
        if (Object.keys(config.userInfo).length > 0) {
            await mergeAndPersistUserInfo(config.userInfo);
        }
        if (config.sessionManager.isEnabled() && config.sessionManager.isNewSession()) {
            await processAndPersistEvent('Session', { subtype: 'session_start' });
        }
        // Start the loop that sends events in this tab if and only if there is no other tab sending events.
        // If other tabs have the lock this request will return an (ignored) promise that will run the
        // `sendNextBatch` function only when the lock is available.
        config.lockRunner('picoSendEvents', sendNextBatch);
        return {
            result: 'success',
            pico: {
                trackExperimentSegmentReceived: async (segments) => {
                    try {
                        const userInfo = await getUserInfo();
                        const oldSegments = (userInfo['experiment'] ?? {});
                        await mergeAndPersistUserInfo({ experiment: segments });
                        if (shouldSendExperimentSegmentReceivedEvent(oldSegments, segments)) {
                            await processAndPersistEvent('ExperimentSegmentReceived', {});
                        }
                    }
                    catch (e) {
                        config.logger.error('An error occurred while tracking experiment segment received.', e);
                    }
                },
                trackUserAction: async (event) => {
                    try {
                        await processAndPersistEvent('UserAction', event);
                    }
                    catch (e) {
                        config.logger.error('An error occurred while tracking user action.', e);
                    }
                },
                trackSignInAction: async (event) => {
                    try {
                        await processAndPersistEvent('SignIn', event);
                    }
                    catch (e) {
                        config.logger.error('An error occurred while tracking sign in action.', e);
                    }
                },
                getUserInfo,
                updateUserInfo: mergeAndPersistUserInfo,
            },
        };
    }
    catch (e) {
        return {
            result: 'error',
            error: e,
        };
    }
};
//# sourceMappingURL=setup.js.map